import { Btn, DataTable, Stack } from "./ui";
import type { DataTableColumn } from "./ui";
import * as Sentry from "@sentry/browser";
import { DisplayStatusIcon } from "./DisplayStatusIcon";
import { DEFAULT_FORM_INPUT_TYPES, LOCAL_STORAGE_KEYS, S3_IMAGE_PATH } from "../utils/constants";
import { iReviewReportProps } from "../types";
import { useIndexedDB } from "react-indexed-db-hook";
import SignaturePad from 'signature_pad';
import { useEffect, useRef, useState } from "react";
import styled from '@emotion/styled';
import { base64ToBlob } from "../utils/helperFunctions";
import { useUserContext } from "../contexts/UserContext";
import { remove, uploadData } from "aws-amplify/storage";
import { v4 as uuidv4 } from 'uuid';
const SignatureCanvas = styled.canvas`
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  height: 150px;
  margin: 20px 0;
  background-color: white;
  
  @media (min-width: 768px) {
    height: 200px;
  }
  
  @media (min-width: 1024px) {
    height: 250px;
  }
`;

export const ReviewReport = ({ cancel, inspectionFormPrompts, media, submit }: iReviewReportProps) => {
  const { company, companyOwner } = useUserContext();
  const { update, getByID, deleteRecord } = useIndexedDB("signature");

  const companyId = (company?.id as string) || companyOwner?.companyId;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [signaturePad, setSignaturePad] = useState<SignaturePad | null>(null);
  const [signatureCaptured, setSignatureCaptured] = useState<boolean>(false);

  const equipmentId = localStorage.getItem(LOCAL_STORAGE_KEYS.INSPECT);
  const unitId = localStorage.getItem(LOCAL_STORAGE_KEYS.UNIT_ID);
  const equipmentName = localStorage.getItem(LOCAL_STORAGE_KEYS.EQUIPMENT_NAME);

  const SAVED_SIGNATURE_KEY = `saved_signature_${companyId}`;

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d')?.scale(ratio, ratio);

      const newSignaturePad = new SignaturePad(canvas, {
        minWidth: 1,
        maxWidth: 2.5,
        penColor: "rgb(66, 133, 244)",
        backgroundColor: "rgb(255, 255, 255)"
      });
      setSignaturePad(newSignaturePad);

      // Automatically load saved signature if it exists
      const savedSignature = localStorage.getItem(SAVED_SIGNATURE_KEY);
      if (savedSignature) {
        newSignaturePad.fromDataURL(savedSignature);
        setSignatureCaptured(true); // Enable submit button immediately
      }
    }
  }, [SAVED_SIGNATURE_KEY]);

  const clearSignature = async () => {
    signaturePad?.clear();
    setSignatureCaptured(false);

    try {

      await deleteRecord(companyId ?? '');

      const storedSignature = localStorage.getItem(LOCAL_STORAGE_KEYS.SIGNATURE_PATH)

      if (storedSignature) {
        try {
          await remove({
            path: storedSignature,
          })
        } catch (error) {
          console.error("Error fetching data:", error);
          Sentry.captureException(error);
        } finally {
          localStorage.removeItem(LOCAL_STORAGE_KEYS.SIGNATURE_PATH)
        }
      }

    } catch (error) {
      console.error("Error fetching data:", error);
      Sentry.captureException(error);
    } finally {
      setSignatureCaptured(false)
    }
  };

  const saveSignature = async () => {
    if (signaturePad && !signaturePad.isEmpty()) {

      try {
        let imagePath;
        const signatureData = signaturePad.toDataURL('image/jpeg', 0.8);
        const blob = await base64ToBlob(signatureData, 'image/jpeg');

        // Save signature to localStorage for future reuse (replaces any existing)
        localStorage.setItem(SAVED_SIGNATURE_KEY, signatureData);

        const signatureObject = {
          id: companyId,
          signatureData: signatureData,
          equipmentId: equipmentId,
          unitId: unitId,
          equipmentName: equipmentName,
        };
        // Upsert (put): the record is keyed on companyId, so add() would throw
        // ConstraintError on a re-sign. update() overwrites the existing record.
        await update(signatureObject);

        if (navigator.onLine) {
          const imageName = `${equipmentId}/${unitId ? `${unitId}/` : ""}${uuidv4()}-SIGNATURE-${equipmentName}.jpeg`;
          const imageFile = new File([blob!], `${imageName}`, { type: 'image/jpeg' });

          const savedImageS3 = await uploadData({
            path: S3_IMAGE_PATH.signatureImagePath(companyId ?? '', imageName),
            data: imageFile,
          }).result;

          imagePath = savedImageS3.path;
          localStorage.setItem(LOCAL_STORAGE_KEYS.SIGNATURE_PATH, imagePath)
        }

      } catch (error) {
        console.error("Error fetching data:", error);
        setSignatureCaptured(false)
        Sentry.captureException(error);

      } finally {
        setSignatureCaptured(true)

      }

    }
  };

  useEffect(() => {
    const loadStoredSignature = async () => {
      try {
        const storedSignature = await getByID(companyId ?? '');
        if (storedSignature && signaturePad) {
          const reader = new FileReader();
          reader.onloadend = () => {
            signaturePad.fromDataURL(reader.result as string);
            setSignatureCaptured(true);
          };
          reader.readAsDataURL(storedSignature.signatureBlob);
        }
      } catch (error) {
        console.error("Error loading stored signature:", error);
        Sentry.captureException(error);
      }
    };

    if (signaturePad) {
      loadStoredSignature();
    }
  }, [signaturePad, companyId]);

  const rows = inspectionFormPrompts.map((prompts, idx) => ({
    ...prompts,
    photo: (media && media[idx]?.length) || 0,
    rowKey: String(idx),
  }));

  const columns: DataTableColumn<(typeof rows)[number]>[] = [
    {
      id: "prompt",
      header: "Prompt",
      renderCell: (item) =>
        item.type === DEFAULT_FORM_INPUT_TYPES.section
          ? `${item.title} / ${item.label}`
          : item.label,
    },
    {
      id: "value",
      header: "Value",
      renderCell: (item) => {
        if ('selectedOptions' in item && item.subType === DEFAULT_FORM_INPUT_TYPES.checkbox && item.selectedOptions) {
          return (
            <div>
              {item.selectedOptions.map((option: string, index: number) => (
                <div key={index}>
                  <DisplayStatusIcon size="big">{option}</DisplayStatusIcon>
                </div>
              ))}
            </div>
          );
        }
        return <DisplayStatusIcon size="big">{item.value}</DisplayStatusIcon>;
      },
    },
    {
      id: "notes",
      header: "Notes",
      renderCell: (item) => item.notes,
    },
    {
      id: "media",
      header: "Media Files",
      renderCell: (item) => item.photo,
    },
  ];

  return (
    <div className="review-report">
      <Stack size="l">
        <DataTable
          ariaLabel="Inspection review"
          columns={columns}
          rows={rows}
          getRowKey={(row) => row.rowKey}
        />
        <div className="review-report-signature">
          <h2>Signature:</h2>
          <Stack size="m">
            <SignatureCanvas ref={canvasRef} />
            <Stack direction="horizontal" size="xs">
              <Btn buttonText="Clear Signature" buttonOnClick={clearSignature} />
              <Btn
                buttonDisabled={signatureCaptured}
                buttonOnClick={saveSignature}
                buttonText={!signatureCaptured ? "Save Signature" : "Saved"}
              />
            </Stack>
          </Stack>
        </div>
        <div className="review-report-actions">
          <Btn buttonText="Cancel" buttonOnClick={cancel} />
          <Btn
            variant="primary"
            buttonText="Submit"
            buttonOnClick={submit}
            buttonDisabled={!signatureCaptured}
          />
        </div>
      </Stack>
    </div>
  );
};
